(() => {
  const containerSelector = '#ProductGridContainer';
  const gridSelector = '#product-grid';
  const paginationSelector = '.pagination-wrapper';
  const nextSelector = '.pagination__item--prev';
  const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');

  let intersectionObserver;
  let loading = false;
  let scrollQueued = false;

  const preservePreviewTheme = (value, base = window.location.href) => {
    const url = new URL(value, base);
    if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);
    return url.href;
  };

  const fetchPage = async (url) => {
    const response = await fetch(preservePreviewTheme(url), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!response.ok) throw new Error(`Unable to load products: ${response.status}`);
    return {
      document: new DOMParser().parseFromString(await response.text(), 'text/html'),
      url: response.url
    };
  };

  const createSentinel = (pagination) => {
    const sentinel = document.createElement('div');
    sentinel.dataset.chicunInfiniteSentinel = '';
    sentinel.setAttribute('role', 'status');
    sentinel.setAttribute('aria-live', 'polite');
    sentinel.style.cssText = 'min-height:1px;grid-column:1/-1;text-align:center;padding:2rem;';
    pagination.before(sentinel);
    return sentinel;
  };

  const setup = () => {
    intersectionObserver?.disconnect();

    const container = document.querySelector(containerSelector);
    const grid = container?.querySelector(gridSelector);
    const pagination = container?.querySelector(paginationSelector);
    const nextLink = pagination?.querySelector(nextSelector);

    container?.querySelector('[data-chicun-infinite-sentinel]')?.remove();
    if (!container || !grid || !pagination || !nextLink) return;

    pagination.hidden = true;
    const sentinel = createSentinel(pagination);
    intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadNextPage();
      },
      { rootMargin: '1200px 0px' }
    );
    intersectionObserver.observe(sentinel);
    checkScrollDistance();
  };

  const loadNextPage = async () => {
    if (loading) return;

    const container = document.querySelector(containerSelector);
    const grid = container?.querySelector(gridSelector);
    let pagination = container?.querySelector(paginationSelector);
    let nextLink = pagination?.querySelector(nextSelector);
    const sentinel = container?.querySelector('[data-chicun-infinite-sentinel]');

    if (!container || !grid || !pagination || !nextLink || !sentinel) return;

    loading = true;
    intersectionObserver?.disconnect();
    sentinel.textContent = 'Loading more';

    try {
      let nextUrl = preservePreviewTheme(nextLink.getAttribute('href'));
      let added = 0;
      let replacementPagination;

      while (nextUrl && added === 0) {
        const page = await fetchPage(nextUrl);
        const nextGrid = page.document.querySelector(`${containerSelector} ${gridSelector}`);
        const items = nextGrid ? Array.from(nextGrid.children) : [];
        items.forEach((item) => grid.append(item));
        added += items.length;

        replacementPagination = page.document.querySelector(
          `${containerSelector} ${paginationSelector}`
        );
        const followingLink = replacementPagination?.querySelector(nextSelector);
        nextUrl = followingLink
          ? preservePreviewTheme(followingLink.getAttribute('href'), page.url)
          : '';
      }

      if (replacementPagination) {
        pagination.replaceWith(replacementPagination);
      } else {
        pagination.remove();
      }
      sentinel.remove();
    } catch (error) {
      console.error(error);
      sentinel.textContent = 'Unable to load more products';
    } finally {
      loading = false;
      setup();
    }
  };

  const checkScrollDistance = () => {
    const sentinel = document.querySelector('[data-chicun-infinite-sentinel]');
    if (!sentinel || loading) return;
    if (sentinel.getBoundingClientRect().top < window.innerHeight + 1200) loadNextPage();
  };

  const onScroll = () => {
    if (scrollQueued) return;
    scrollQueued = true;
    window.requestAnimationFrame(() => {
      scrollQueued = false;
      checkScrollDistance();
    });
  };

  let setupTimer;
  const scheduleSetup = () => {
    window.clearTimeout(setupTimer);
    setupTimer = window.setTimeout(setup, 50);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup, { once: true });
  } else {
    setup();
  }

  new MutationObserver((mutations) => {
    if (
      mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.(containerSelector) || node.querySelector?.(containerSelector))
        )
      )
    ) {
      scheduleSetup();
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
